import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { Section, Eyebrow } from "@/components/common";
import { CONTACT, PILLARS, BROCHURE } from "@/data/content";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const initial = { name: "", email: "", company: "", service_interest: "", message: "" };

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.message.trim()) e.message = "Tell us a little about your goals.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source: "contact" });
      setSuccess(true);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (k) =>
    `w-full rounded-xl border bg-[#F3F4F6] px-4 py-3 text-sm text-[#0A0A0A] placeholder:text-[#6B7280] focus:outline-none ${
      errors[k] ? "border-destructive" : "border-[#E5E7EB] focus:border-brand-violet"
    }`;

  return (
    <div>
      <Seo title="Contact" description="Book a consultation with ERAM DIGITAL. Tell us your goals and our specialists will map the right approach for you." path="/contact" />

      <Section className="py-20">
        <div className="grid gap-14 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Contact</Eyebrow>
            <h1 className="font-display text-5xl font-semibold leading-tight tracking-tighter text-[#0A0A0A] sm:text-6xl">
              Let's build something <span className="gradient-text">exceptional.</span>
            </h1>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-[#4B5563]">
              Share your goals and a specialist will reach out to scope the right pillar and engagement model.
            </p>

            <div className="mt-10 space-y-5">
              {[
                { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, id: "contact-email-link" },
                { icon: Phone, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}`, id: "contact-phone-link" },
                { icon: Phone, label: "Additional phone", value: CONTACT.secondaryPhone, href: `tel:${CONTACT.secondaryPhone.replace(/\s/g, "")}`, id: "contact-secondary-phone-link" },
                { icon: MapPin, label: "Head office", value: CONTACT.hq, id: "contact-head-office" },
                { icon: MapPin, label: "Regional offices", value: `${CONTACT.uae} · ${CONTACT.bahrain}`, id: "contact-regional-offices" },
                { icon: Clock, label: "Working hours", value: CONTACT.hours, id: "contact-hours" },
              ].map((c) => (
                <div key={c.label} className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] text-brand-violet"><c.icon className="h-5 w-5" /></div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-widest text-[#6B7280]">{c.label}</p>
                    {c.href ? <a href={c.href} className="break-words text-[#0A0A0A] hover:text-brand-violet" data-testid={c.id}>{c.value}</a> : <p className="text-[#0A0A0A]" data-testid={c.id}>{c.value}</p>}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="gradient-border rounded-3xl p-8 sm:p-10">
              {success ? (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex min-h-[420px] flex-col items-center justify-center text-center" data-testid="contact-success">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full gradient-bg"><CheckCircle2 className="h-8 w-8 text-[#0A0A0A]" /></div>
                  <h3 className="mt-6 font-display text-2xl font-semibold text-[#0A0A0A]">Thank you.</h3>
                  <p className="mt-3 max-w-sm text-[#4B5563]">Your inquiry has been saved for our team to review. Thank you for telling us about your goals.</p>
                  <button onClick={() => setSuccess(false)} className="mt-8 rounded-full border border-[#E5E7EB] px-6 py-2.5 text-sm font-semibold text-[#0A0A0A] hover:bg-[#F3F4F6]" data-testid="contact-reset">Send another message</button>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form" noValidate>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-name" className="mb-2 block text-sm font-medium text-[#4B5563]">Full name *</label>
                      <input id="contact-name" name="name" autoComplete="name" maxLength={120} required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls("name")} placeholder="Your name" data-testid="contact-name" />
                      {errors.name && <p className="mt-1 text-xs text-destructive" data-testid="error-name">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="mb-2 block text-sm font-medium text-[#4B5563]">Email *</label>
                      <input id="contact-email" name="email" type="email" autoComplete="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls("email")} placeholder="you@company.com" data-testid="contact-email" />
                      {errors.email && <p className="mt-1 text-xs text-destructive" data-testid="error-email">{errors.email}</p>}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="mb-2 block text-sm font-medium text-[#4B5563]">Company</label>
                    <input id="contact-company" name="company" autoComplete="organization" maxLength={160} value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls("company")} placeholder="Company name" data-testid="contact-company" />
                  </div>
                  <div>
                    <label htmlFor="contact-service" className="mb-2 block text-sm font-medium text-[#4B5563]">Service interest</label>
                    <select id="contact-service" name="service_interest" value={form.service_interest} onChange={(e) => set("service_interest", e.target.value)} className={inputCls("service_interest")} data-testid="contact-service">
                      <option value="" className="bg-white">Select a service…</option>
                      {PILLARS.map((p) => <option key={p.id} value={p.name} className="bg-white">{p.name}</option>)}
                      <optgroup label="Industrial & enterprise capabilities">{BROCHURE.capabilities.map((cap) => <option key={cap.id} value={cap.name}>{cap.name}</option>)}</optgroup>
                      <option value="Other" className="bg-white">Something else</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="mb-2 block text-sm font-medium text-[#4B5563]">Message *</label>
                    <textarea id="contact-message" name="message" maxLength={4000} required rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls("message")} placeholder="Tell us about your goals…" data-testid="contact-message" />
                    {errors.message && <p className="mt-1 text-xs text-destructive" data-testid="error-message">{errors.message}</p>}
                  </div>
                  <button type="submit" disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-full gradient-bg py-3.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60" data-testid="contact-submit">
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin" />Sending…</> : t("cta.consult")}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
